# THE NODE AUDIO WARM-UP — card 6aaa14f67fc38cab6b35ae09.
#
# WHAT WAS HAPPENING. Node.getManifest was asked for ONE node, in
# NodeDisplay's componentDidMount — that is, when the question is already on
# the screen. Two costs followed from that, and the second is the expensive
# one:
#
#   * every spoken line started its CloudFront download at the moment it was
#     about to be said, so the child waited on network latency per line; and
#   * a node whose audio has never been generated discovered the gap at the
#     moment the child needed to hear it. getManifest queues the generation,
#     the GPU takes as long as it takes, and THAT question is spoken by the
#     browser's own voice.
#
# So the warm-up is not really about download time. It is about GENERATION
# LEAD TIME: ask for every node in the profile's active set at the start, and
# a gapped node has its audio queued long before chance can choose it.
#
# WHERE IT STARTS. At the game-list click — the same moment the images are
# queued (GameList/games.coffee) — so the child reaches the FIRST question
# already warm rather than warming during its own greeting. game_layout picks
# the same cache up on the way in and keeps it topped up per answer, which is
# why the cache lives in this module and not in either component: navigating
# from the list into play must not drop it.
#
# WHAT IT DOES NOT TOUCH. The say() path. The audio is warmed into the
# browser's ordinary HTTP cache, so instruction_set's existing
# registerSound/play/removeSound cycle finds the file already there and is
# otherwise unchanged — the autoplay handling, the playback-failure tiers and
# the greeting blacklist all still run exactly as they did.
import {AUDIO_CDN_BASE, AUDIO_CDN_QUERY} from './audio_cdn.coffee'

# Bounds, all of them the ones the owner set when he accepted this work.
#
# CLIP_BUDGET is a whole session's warm-up, not a per-node one: the point is a
# ceiling on what a parent's connection is asked to carry for audio nobody has
# asked to hear yet. The owner set that ceiling as 24 MB; it is counted in
# CLIPS rather than bytes because the warm-up's response is now opaque and its
# body cannot be measured (see pump below). A Piper line is a few tens of KB,
# so 24 MB at ~40 KB a line is the number here — hundreds of lines, comfortably
# a large active set, and a runaway one stops. Card 6ab6d3fab0423d086b66d0d0.
CLIP_BUDGET = 600
# Enough to keep the pipe busy, few enough to leave room for the images the
# game list is loading through its own queue at the same moment.
MAX_CONCURRENT = 4
# The server takes the active set in one call; this is its cap (Node.coffee
# MANIFEST_BATCH_LIMIT). Stated here so a larger set is split rather than
# silently truncated at the far end.
MAX_IDS_PER_CALL = 100

# WHEN NOT TO WARM AT ALL. Save-Data is the browser telling us the person has
# asked for less; a cellular or 2g connection is the same answer arrived at
# from the other side. Manifests are still fetched in both cases — they are
# tiny JSON maps and they are what queues the generation, which is the half
# that matters most — but nothing is downloaded ahead of being needed.
shouldWarmAudio = ->
  conn = navigator?.connection or navigator?.mozConnection or navigator?.webkitConnection
  return true unless conn?
  return false if conn.saveData
  return false if conn.type is 'cellular'
  return false if conn.effectiveType in ['slow-2g', '2g']
  true

cacheKey = (student, game) ->
  voiceIds = (String(v) for v in (game?.piperVoiceIds or [])).sort().join(',')
  [String(student?.id), String(game?.id), String(student?.preferred_language or ''),
   String(student?.piperVoiceId or ''), voiceIds].join('|')

state =
  key: null
  manifests: {}      # nodeId -> the manifest getManifests returned
  requested: {}      # nodeId -> true, so a delta never re-asks
  warmed: {}         # url -> true
  clips: 0
  queue: []
  running: 0
  aborter: null

# Everything about the cached set belongs to one child playing one game with
# one voice pinned. Anything else and the manifests are about somebody else.
reset = (key = null) ->
  state.aborter?.abort()
  state.key = key
  state.manifests = {}
  state.requested = {}
  state.warmed = {}
  state.clips = 0
  state.queue = []
  state.running = 0
  state.aborter = if key? and typeof AbortController isnt 'undefined' then new AbortController() else null

export clearWarmedManifests = -> reset null

# The manifest for a node, if the warm-up already has it. NodeDisplay asks
# this first and falls back to its own single-node getManifest on a miss, so
# nothing regresses when the warm-up did not run, was aborted, or skipped this
# node.
export manifestForNode = (nodeId) ->
  return null unless nodeId?
  state.manifests[String(nodeId)] or null

pump = ->
  while state.running < MAX_CONCURRENT and state.queue.length > 0
    url = state.queue.shift()
    continue if state.warmed[url]
    break if state.clips >= CLIP_BUDGET
    state.warmed[url] = true
    state.running += 1
    do (url) ->
      # ONE CORS REQUEST FOR EVERY CLIP — owner 2026-09-26, reverting the
      # no-cors half of card 6ab6d3fab0423d086b66d0d0. SoundJS (iOS app and
      # Safari, tried first) loads with a CORS XHR, and <audio> sets
      # crossOrigin 'anonymous'; this asks the same way, on the same
      # AUDIO_CDN_QUERY url, so the entry it fills is the entry both read. A
      # no-cors warm-up cached CloudFront's no-Origin copy (no allow-origin, no
      # `Vary: Origin`) and SoundJS's CORS load was refused it: iOS and Safari
      # heard browser speech on every warmed question (production 38dacce).
      # Reproduced in Chromium against the live CDN: no-cors fetch then CORS
      # XHR -> CORS error; the XHR alone -> 200. See audio_cdn.coffee.
      #
      # The body is not read: what is kept is the HTTP cache entry.
      fetch(url, {mode: 'cors', credentials: 'omit', signal: state.aborter?.signal})
        .then ->
          state.clips += 1
        .catch (err) ->
          # An abort is the warm-up being superseded, which is normal. Anything
          # else is one file that will be fetched at say() time as before.
          return if err?.name is 'AbortError'
          delete state.warmed[url]
        .then ->
          state.running -= 1
          pump()

# Priority order: the node the child is about to be given, then the rest.
# `first` is the chosen node when there is one — at the game-list click there
# is not, and the order within the active set is then the order the server
# answered in.
warmAudio = (nodeIds, first = null) ->
  return unless shouldWarmAudio()
  ordered = if first? and String(first) in nodeIds then [String(first)].concat(n for n in nodeIds when n isnt String(first)) else nodeIds
  for nodeId in ordered
    manifest = state.manifests[nodeId]
    continue unless manifest?.audios?
    for md5Key, entry of manifest.audios
      continue unless entry?.s3Key
      state.queue.push "#{AUDIO_CDN_BASE}#{entry.s3Key}#{AUDIO_CDN_QUERY}"
  pump()

chunk = (list, size) ->
  out = []
  out.push list.slice(i, i + size) for i in [0...list.length] by size
  out

# THE ENTRY POINT. Fetch manifests for every node in `nodeIds` this cache does
# not already hold, then warm their audio. Safe to call on every pass/fail:
# the ids it already has are the delta rule, and asking again costs nothing.
#
# Never rejects and never blocks its caller — it runs beside a game the child
# is already starting, and a warm-up that can fail a game start is worse than
# no warm-up.
export warmManifests = ({student, game, nodeIds, chosenNodeId} = {}) ->
  return Promise.resolve(false) unless student?.id and game?.id and nodeIds?.length

  key = cacheKey student, game
  reset(key) unless state.key is key

  wanted = (String(n) for n in nodeIds when n? and not state.requested[String(n)])
  if wanted.length is 0
    warmAudio (String(n) for n in nodeIds), chosenNodeId
    return Promise.resolve(true)
  state.requested[n] = true for n in wanted

  lbapi = window.app?.loopbackAPI
  return Promise.resolve(false) unless lbapi?.Node?.getManifests?

  args = (ids) -> {
    ids: ids
    gameId: game.id
    voiceIds: game.piperVoiceIds or []
    locale: student.preferred_language
    piperVoiceId: student.piperVoiceId or undefined
    studentId: student.id
  }

  batches = chunk wanted, MAX_IDS_PER_CALL
  Promise.all(batches.map (ids) -> lbapi.Node.getManifests(args(ids)))
    .then (responses) ->
      return false unless state.key is key   # superseded while in flight
      arrived = []
      for response in responses
        for nodeId, manifest of (response?.data?.manifests or {})
          state.manifests[nodeId] = manifest
          arrived.push nodeId
        for miss in (response?.data?.skipped or [])
          # Let it be asked for again: the per-node fallback will try, and so
          # will the next delta.
          delete state.requested[String(miss.nodeId)] if miss?.nodeId?
      console.log "🎙️ warmed #{arrived.length} node manifest(s) of #{wanted.length} asked"
      warmAudio arrived, chosenNodeId
      true
    .catch (err) ->
      # The whole point of the warm-up is that nothing depends on it. Let the
      # ids be asked for again and leave the per-node path to do its job.
      console.warn "manifest warm-up failed (non-blocking):", err
      delete state.requested[n] for n in wanted
      false

# For pins: what the cache is actually holding.
export warmState = -> {key: state.key, nodes: Object.keys(state.manifests), queued: state.queue.length, clips: state.clips, warmed: Object.keys(state.warmed).length}
